// components/WaterLevelSensorHistory/WaterLevelErrorHistory.js

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Stack,
  Avatar,
  Divider,
  Button,
  ButtonGroup,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorIcon from '@mui/icons-material/Error';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import BuildIcon from '@mui/icons-material/Build';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import { API_BASE_URL } from '../../../config/config';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

// Define the error types with their display info
const errorTypesConfig = {
  'Error': {
    label: 'General Error',
    icon: <ErrorIcon />,
    color: '#f44336'
  },
  'noSignal': {
    label: 'No Signal',
    icon: <SignalWifiOffIcon />,
    color: '#ff9800'
  },
  'sensorFault': {
    label: 'Sensor Fault',
    icon: <BuildIcon />,
    color: '#9c27b0'
  },
  'lowBattery': {
    label: 'Low Battery',
    icon: <BatteryAlertIcon />,
    color: '#3f51b5'
  }
};

const WaterLevelErrorHistory = () => {
  const { device_id, subid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [errorLogs, setErrorLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sensorInfo, setSensorInfo] = useState(null);
  
  // Date filtering states
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [dateFilter, setDateFilter] = useState('today'); // 'today', 'yesterday', 'week', 'month', 'custom', 'all'

  // Fetch error logs from API
  const fetchErrorLogs = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}api/sensor/waterlevel/${device_id}/${subid}/error-logs`;
      const params = new URLSearchParams();

      // Add date parameters based on filter type
      if (dateFilter === 'all') {
        params.append('all', 'true');
      } else if (dateFilter === 'custom') {
        params.append('fromDate', fromDate.format('DD-MM-YYYY'));
        params.append('toDate', toDate.format('DD-MM-YYYY'));
      } else {
        // For predefined filters, calculate dates
        const { from, to } = getDateRange(dateFilter);
        params.append('fromDate', from.format('DD-MM-YYYY'));
        params.append('toDate', to.format('DD-MM-YYYY'));
      }

      const response = await axios.get(`${url}?${params.toString()}`);
      
      setErrorLogs(response.data.errorLogs || []);
      setSensorInfo(response.data.sensorInfo || {});
    } catch (err) {
      console.error('Error fetching error logs:', err);
      setError(err.response?.data?.message || 'Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  };

  // Get date range based on filter type
  const getDateRange = (filterType) => {
    const today = dayjs();
    switch (filterType) {
      case 'today':
        return { from: today, to: today };
      case 'yesterday':
        const yesterday = today.subtract(1, 'day');
        return { from: yesterday, to: yesterday };
      case 'week':
        return { from: today.subtract(7, 'day'), to: today };
      case 'month':
        return { from: today.subtract(30, 'day'), to: today };
      default:
        return { from: today, to: today };
    }
  };

  // Handle date filter change
  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    if (newFilter !== 'custom') {
      const { from, to } = getDateRange(newFilter);
      setFromDate(from);
      setToDate(to);
    }
  };

  // Fetch error logs when dependencies change
  useEffect(() => {
    fetchErrorLogs();
  }, [device_id, subid, dateFilter, fromDate, toDate]);

  // Format duration
  const formatDuration = (startTime, endTime) => {
    if (!startTime) return 'N/A';
    
    const start = dayjs(startTime);
    const end = endTime ? dayjs(endTime) : dayjs();
    const diff = end.diff(start);
    
    if (diff < 0) return 'N/A';
    
    const duration = dayjs.duration(diff);
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();
    
    let result = '';
    if (days > 0) result += `${days}d `;
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}m`;
    
    return result.trim() || '< 1m';
  };

  // Format time in IST
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return dayjs(timestamp).tz('Asia/Kolkata').format('DD/MM/YYYY HH:mm:ss');
  };

  // Get status chip
  const getStatusChip = (startTime, endTime) => {
    const isRunning = startTime && !endTime;
    return (
      <Chip
        icon={isRunning ? <PlayArrowIcon /> : <StopIcon />}
        label={isRunning ? 'RUNNING' : 'RESOLVED'}
        color={isRunning ? 'error' : 'success'}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // Mobile Card Component
  const ErrorCard = ({ log }) => (
    <Card sx={{ mb: 2, borderLeft: `4px solid ${errorTypesConfig[log.errorType]?.color || '#757575'}` }}>
      <CardContent>
        <Stack spacing={2}>
          {/* Error Type Header */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ bgcolor: errorTypesConfig[log.errorType]?.color, width: 32, height: 32 }}>
              {errorTypesConfig[log.errorType]?.icon}
            </Avatar>
            <Box flex={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {errorTypesConfig[log.errorType]?.label || log.errorType}
              </Typography>
            </Box>
            {getStatusChip(log.startTime, log.endTime)}
          </Stack>

          <Divider />

          {/* Time Information */}
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PlayArrowIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">Start Time:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatTime(log.startTime)}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <StopIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">End Time:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {log.endTime ? formatTime(log.endTime) : 'Still Running'}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <AccessTimeIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">Duration:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatDuration(log.startTime, log.endTime)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 2 : 3}>
      {/* Header with Back Button */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Tooltip title="Back to Sensor History">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
          Error History
        </Typography>
      </Stack>

      {/* Sensor Information */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Device ID
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {device_id}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Sensor Sub ID
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {subid}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Total Errors
            </Typography>
            <Typography variant="h6" fontWeight={600} color="error.main">
              {errorLogs.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Date Filter Controls */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Filter Options
        </Typography>
        
        {/* Quick Date Filters */}
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Quick Filters
          </Typography>
          <ButtonGroup 
            variant="outlined" 
            size="small" 
            sx={{ 
              flexWrap: 'wrap', 
              gap: 1,
              '& .MuiButtonGroup-grouped': {
                border: '1px solid',
                borderColor: 'divider',
                '&:not(:last-of-type)': {
                  borderRight: '1px solid',
                  borderRightColor: 'divider',
                }
              }
            }}
          >
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: 'week', label: 'Last 7 Days' },
              { key: 'month', label: 'Last 30 Days' },
              { key: 'all', label: 'All Logs' }
            ].map((filter) => (
              <Button
                key={filter.key}
                variant={dateFilter === filter.key ? 'contained' : 'outlined'}
                onClick={() => handleDateFilterChange(filter.key)}
                sx={{ mb: isMobile ? 1 : 0 }}
              >
                {filter.label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {/* Custom Date Range */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Custom Date Range
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    setDateFilter('custom');
                  }}
                  maxDate={toDate}
                  renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => {
                    setToDate(newValue);
                    setDateFilter('custom');
                  }}
                  minDate={fromDate}
                  renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setDateFilter('custom')}
                disabled={!fromDate || !toDate}
              >
                Apply Custom Range
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Error Logs */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box p={2} bgcolor="primary.main" color="white">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Error Log History
              </Typography>
              <Typography variant="body2" opacity={0.9}>
                All times shown in Indian Standard Time (IST)
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" opacity={0.9}>
                {dateFilter === 'all' 
                  ? 'All Records' 
                  : dateFilter === 'custom'
                  ? `${fromDate.format('DD/MM/YYYY')} - ${toDate.format('DD/MM/YYYY')}`
                  : dateFilter === 'today'
                  ? 'Today'
                  : dateFilter === 'yesterday' 
                  ? 'Yesterday'
                  : dateFilter === 'week'
                  ? 'Last 7 Days'
                  : 'Last 30 Days'
                }
              </Typography>
              <Typography variant="caption" opacity={0.7}>
                {errorLogs.length} {errorLogs.length === 1 ? 'record' : 'records'} found
              </Typography>
            </Box>
          </Stack>
        </Box>

        {errorLogs.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Error Logs Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This sensor has no recorded error events.
            </Typography>
          </Box>
        ) : isMobile ? (
          // Mobile View - Cards
          <Box p={2}>
            {errorLogs.map((log, index) => (
              <ErrorCard key={index} log={log} />
            ))}
          </Box>
        ) : (
          // Desktop View - Table
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Error Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorLogs.map((log, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:hover': { bgcolor: 'grey.50' },
                      borderLeft: `4px solid ${errorTypesConfig[log.errorType]?.color || '#757575'}`
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ bgcolor: errorTypesConfig[log.errorType]?.color, width: 32, height: 32 }}>
                          {errorTypesConfig[log.errorType]?.icon}
                        </Avatar>
                        <Typography fontWeight={500}>
                          {errorTypesConfig[log.errorType]?.label || log.errorType}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTime(log.startTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {log.endTime ? formatTime(log.endTime) : 'Still Running'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDuration(log.startTime, log.endTime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(log.startTime, log.endTime)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default WaterLevelErrorHistory;
